/**
 * BdrManual — /#/manuals/bdr
 * Long-scroll, well-typeset HTML manual mirroring the BDR markdown source.
 *
 * Brand locks: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No exclamation points. No emojis. Never "rep".
 *
 * W66.X2 rewrites:
 *   - Fixed CP3 labeled as "Pain" → "Budget" throughout M pillar section and glossary
 *   - Fixed Composite score / M-PACT conflation (1B)
 *   - Fixed "6-step program" → "7-touch program" to match 7-row cadence table (1E)
 *   - Fixed "Marketing is the gate" → "Marketing holds approval" (1F)
 *   - Fixed "approval gates" → "approval checkpoints" in CP6 table (CP table)
 *   - Fixed "six tabs" → "seven tabs" in account detail section (4D)
 *   - Removed Quick Score button reference as general update button (1C)
 *   - Corrected handoff step — Gate0Qualifier flow, not generic Quick Score (1D)
 */

import ManualLayout, {
  ManualSectionHeading,
  Callout,
  ManualImage,
  StepList,
  BulletList,
  ManualPara,
  ManualTable,
  type ManualSection,
} from "@/components/manuals/ManualLayout";

import shot00 from "@/assets/manuals/bdr/00-login.png";
import shot01 from "@/assets/manuals/bdr/01-dashboard-bdr.png";
import shot02 from "@/assets/manuals/bdr/02-bdr-cadence-queue.png";
import shot03 from "@/assets/manuals/bdr/03-bdr-prospect-detail.png";
import shot04 from "@/assets/manuals/bdr/04-bdr-activity-log.png";
import shot05 from "@/assets/manuals/bdr/05-bdr-handoff-to-ae.png";
import shot06 from "@/assets/manuals/bdr/06-bdr-plan-vs-actual.png";

const SECTIONS: ManualSection[] = [
  { id: "welcome",       label: "Welcome and how to use this manual" },
  { id: "role",          label: "The role: what a BDR owns in M-PACT" },
  { id: "login",         label: "Logging in and navigating" },
  { id: "dashboard",     label: "Your dashboard: every tile" },
  { id: "cadence-queue", label: "The Cadence Queue: working today's list" },
  { id: "working",       label: "Working a prospect: new lead to handoff" },
  { id: "handoff",       label: "The handoff: delivering a clean qualified prospect" },
  { id: "activity-log",  label: "Your activity log" },
  { id: "plan-vs-actual", label: "Plan vs actual and the leaderboard" },
  { id: "glossary",      label: "Glossary, gotchas, and closing note" },
];

const BLACK = "#0B0B0F";

export default function BdrManual() {
  return (
    <ManualLayout
      role="BDR"
      title="Attilius Operating Manual — BDR"
      meta="BDR role · v1.1 · June 2026"
      sections={SECTIONS}
    >
      {/* ── Chapter 1 — Welcome ──────────────────────────────────────────────── */}
      <ManualSectionHeading id="welcome" num={1} title="Welcome and how to use this manual" />

      <ManualPara>
        This manual covers one role: the BDR. It does not cover AE quota management, Manager
        pipeline reviews, or Marketing hook approval. Those have their own manuals. This one is yours.
      </ManualPara>

      <ManualPara>
        Read it once from beginning to end before your first session on the platform. Then use it as
        a reference — the chapter structure mirrors the Attilius navigation so you can find what you need
        when you need it.
      </ManualPara>

      <BulletList
        items={[
          <span>M-PACT is the operating model. You work inside it. You do not invent a parallel process alongside it.</span>,
          <span>The 10 checkpoints (CP1 through CP10) are the qualification spine. As a BDR, your direct scope is CP1 through CP3. Everything you gather in those three checkpoints is what makes the handoff to the AE defensible.</span>,
          <span>Attilius is the platform. The two marks are distinct: the elephant is the platform mark, the helix is the diagnostic mark.</span>,
          <span>Manufacturing is the only context here. Every example in this manual uses manufacturing companies — Tier 1 OEMs, first-tier suppliers, plant managers, procurement leads, operations directors.</span>,
        ]}
      />

      {/* ── Chapter 2 — Role ─────────────────────────────────────────────────── */}
      <ManualSectionHeading id="role" num={2} title="The role: what a BDR owns inside M-PACT" />

      <ManualPara>
        The BDR owns the front of the funnel. You are the first human voice a prospect hears from
        Mi6. You are judged on three outcomes: meetings booked with qualified prospects, clean
        handoffs to the AE with CP1 and CP2 answered in the prospect's own words, and cadence
        execution with every touch logged and every overdue cleared within 24 hours.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        How a BDR maps to M-PACT
      </h3>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M — Methodology.</strong> You own the first three checkpoints. CP1 — Account Profile: is there a real project, and is it a new logo or an expansion. CP2 — Compelling Pain: the pain named, quantified, in the prospect's own words. CP3 — Budget: gather budget context only if the prospect volunteers it. You do not skip to CP4 — Timeline or CP5 — Strategic Fit — those belong to the AE.</span>,
          <span><strong style={{ color: BLACK }}>P — Process / Prioritization.</strong> Run the cadence as built. The 7-touch program is not a suggestion. Do not shortcut step 2 because you think the prospect is warm. The cadence is the process.</span>,
          <span><strong style={{ color: BLACK }}>A — Accountability.</strong> Log activity daily. The activity log is not optional. Three blank days in a row is a signal visible to your manager. Two blank days is a yellow flag.</span>,
          <span><strong style={{ color: BLACK }}>C — Cadence / Coaching.</strong> Attend the Friday MQL reconciliation review with Marketing and Manager. Your acceptance rate on Marketing MQLs is a public metric.</span>,
          <span><strong style={{ color: BLACK }}>T — Timing / Timeline management.</strong> Know the firm or formal timeline for every prospect you carry. A firm timeline is a date the buyer has committed to. It lives under CP4 — Timeline. It is never a standalone field.</span>,
        ]}
      />

      <ManualTable
        headers={["Pillar", "What it means for a BDR"]}
        rows={[
          ["M — Methodology", "Own CP1, CP2, CP3. Gather CP4–CP5 context only if volunteered."],
          ["P — Process / Prioritization", "Run the 7-touch cadence as built. No skips, no shortcuts."],
          ["A — Accountability", "Log every dial, email, LinkedIn touch, and outcome daily."],
          ["C — Coaching", "Attend Friday MQL reconciliation. Accept or return with reason."],
          ["T — Timeline", "Know the firm or formal timeline for every active prospect."],
        ]}
      />

      {/* ── Chapter 3 — Login ────────────────────────────────────────────────── */}
      <ManualSectionHeading id="login" num={3} title="Logging in and navigating the platform" />

      <ManualPara>
        Route — <code>https://aip.mi6group.com</code>
      </ManualPara>

      <ManualImage
        src={shot00}
        alt="Attilius login screen"
        caption="Live screen: aip.mi6group.com — login"
      />

      <StepList
        steps={[
          <span>Navigate to <code>https://aip.mi6group.com</code>.</span>,
          <span>Enter the email address your manager provided for your account.</span>,
          <span>Enter your password. If this is your first login, click "Sign in with magic link instead" and check your inbox.</span>,
          <span>Click <strong>Sign in</strong>.</span>,
          <span>You land on the BDR view. The top header reads "ATTILIUS · REVENUE LIFECYCLE" and your name badge shows your role (BDR).</span>,
          <span>The top navigation gives you access to: Overview, Discovery, Sales Path, Question Bank, Templates, Activity Log, Cadence, Opportunity Calculator, and Manual.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Navigation orientation
      </h3>

      <ManualTable
        headers={["Screen", "Route"]}
        rows={[
          ["BDR dashboard", "/#/dashboards/bdr"],
          ["Today's cadence queue", "/#/cadence/bdr"],
          ["Activity log", "/#/log/bdr"],
          ["Accounts list", "/#/accounts"],
          ["Account detail", "/#/account/:id"],
          ["Plan vs Actual", "/#/plan-vs-actual"],
          ["Templates", "/#/templates"],
        ]}
      />

      {/* ── Chapter 4 — Dashboard ────────────────────────────────────────────── */}
      <ManualSectionHeading id="dashboard" num={4} title="Your dashboard: what every tile means" />

      <ManualPara>
        Route — <code>/#/dashboards/bdr</code> (top nav: Overview tab, then BDR tab). Walk through
        every region in this order, every morning.
      </ManualPara>

      <ManualImage
        src={shot01}
        alt="BDR dashboard"
        caption="Live screen: /#/dashboards/bdr"
      />

      <StepList
        steps={[
          <span><strong>Top header strip — role context and period.</strong> The header confirms you are in the BDR view with the current quarter shown.</span>,
          <span><strong>Four action buttons — LOG ACTIVITY · OPEN TODAY'S QUEUE · TEMPLATES · ADD LEAD.</strong> Open Today's Queue first. Log Activity last.</span>,
          <span><strong>Four metric tiles — Dials / MQLs Worked / On-Schedule / 30-Day Pacing.</strong> Dials: total outbound dials logged. MQLs Worked: how many MQLs you have accepted and worked. On-Schedule: percentage of cadence steps completed on time — target 90%+. 30-Day Pacing: trailing 30-day dial-and-connect pacing.</span>,
          <span><strong>Action Queue — Top 5 cadence touches due now.</strong> The five most urgent cadence actions sorted by overdue first. Work this list before opening the full cadence queue.</span>,
          <span><strong>Morning Brief — right-side panel.</strong> The Attilius-generated summary of the three most important things in your territory today. Read it, do not dismiss it.</span>,
          <span><strong>Hot Accounts — quick-access tiles.</strong> Your four highest-composite-score active accounts. Red score means the account is at risk. Open the account detail to update any account's checkpoint answers.</span>,
          <span><strong>Coaching Nudge.</strong> Attilius writes a single-line coaching note based on your week-to-date pattern. It reads patterns, not personalities.</span>,
          <span><strong>Top 10 ABM — Accounts I Touch.</strong> Your Top 10 ABM hunt list. Each tile shows account name, type, and score as a fraction. Click any tile to open the account detail.</span>,
          <span><strong>Today — Where You Live panel.</strong> The cadence queue summary — active in cadence, marketing hook prospects, and parked.</span>,
          <span><strong>Cadence Step breakdown.</strong> The step-by-step count showing which step most prospects are sitting at today.</span>,
          <span><strong>Activity — Your Page.</strong> Trailing-period activity summary showing Dials, Connects, and Meetings Booked. Today / Week / Month / Quarter / Year toggle.</span>,
          <span><strong>Conversion Ladder — Today.</strong> Your personal funnel for today: Dials → Connects → Conversations → Suspects → Prospects. The industry baseline is roughly 8% dial-to-conversation.</span>,
        ]}
      />

      {/* ── Chapter 5 — Cadence Queue ────────────────────────────────────────── */}
      <ManualSectionHeading id="cadence-queue" num={5} title="The Cadence Queue: working today's list" />

      <ManualPara>
        Routes — <code>/#/cadence</code> (top nav: Cadence, first link for BDR role) and
        <code>/#/cadence/bdr</code> (OPEN TODAY'S QUEUE button on the dashboard). The cadence
        queue is where you spend the bulk of your active selling time. It is not a list of names
        — it is a prioritized execution engine.
      </ManualPara>

      <ManualImage
        src={shot02}
        alt="BDR cadence queue"
        caption="Live screen: /#/cadence/bdr — Today's Queue"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        The BDR Cadence Engine
      </h3>

      <ManualTable
        headers={["Step", "Day", "Channel", "What you do"]}
        rows={[
          ["Step 1", "Day 0", "Email", "Send opening email. Personalized intro with hook. Lead with the prospect's situation, not your product."],
          ["Step 2", "Day 2", "Phone", "First call attempt. If no answer, hang up. No voicemail on step 2."],
          ["Step 3", "Day 4", "LinkedIn", "Send a LinkedIn DM. Reference the email you sent."],
          ["Step 4", "Day 6", "Phone", "Second call. Still no voicemail — hang up if no answer."],
          ["Step 5", "Day 9", "Email", "Follow-up email. Different angle from opener. Value drop, reference of prior touches."],
          ["Step 6", "Day 12", "Phone", "Leave an acknowledgment voicemail. Script: \"Hi [Name], this is [BDR] from Mi6 Group. No need to call me back. I'll follow up in the next couple weeks. Wanted to keep myself on your radar in case [hook reason].\""],
          ["Step 7", "Day 14+", "Decision", "Pursuit Decision. Choose: invest in a marketing hook, park 90 days, or disqualify."],
        ]}
      />

      <ManualPara>
        <strong>Auto-progression:</strong> When you click "Log complete" on any step, Attilius records
        the action and advances the enrollment to the next step, scheduling the next touch two days
        out. You do not manually schedule follow-ups. Use "Reschedule" if you need to push a step
        to the following day, or "Skip" to bypass a step and keep the sequence intact.
      </ManualPara>

      <ManualPara>
        Step 7 is a pursuit decision, not a contact action. The 7-touch program includes six outbound
        touches (Steps 1–6) followed by a deliberate decision point at Step 7: continue with a
        marketing hook, park the prospect for 90 days, or disqualify.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Working the queue
      </h3>

      <StepList
        steps={[
          <span>Read the prospect row — name, company, step type (Call / Email / LinkedIn), and due date.</span>,
          <span>Review the action description — it shows the CP-aligned prompt for the current step (for example, "CP1 — Account Profile: First contact required — make outbound call").</span>,
          <span>Execute the touch (send the email, make the call, send the LinkedIn message).</span>,
          <span>Click <strong>Log complete</strong> (green). The step is recorded and advances to the next scheduled touch.</span>,
          <span>If you cannot execute today, click <strong>Skip</strong> (gray) to record the skip, or <strong>Reschedule</strong> (yellow) to move it to tomorrow.</span>,
          <span>Click <strong>Open prospect</strong> to navigate to the full account detail at <code>/#/account/:id</code>.</span>,
          <span>If the call connected and you had a substantive conversation, open <code>/#/log/bdr</code> and record the outcome separately in the activity log.</span>,
        ]}
      />

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Common gotchas.</strong> If a step is overdue (red border), clear it before working new-day steps. "Log complete" records execution but does not capture what happened on the call — if a prospect picked up, log the conversation separately in the activity log. Use the filter pills (All, Calls, Emails, LinkedIn, Other) to focus on one channel at a time. The "Sort by priority" toggle moves overdue steps to the top.
      </Callout>

      {/* ── Chapter 6 — Working a Prospect ───────────────────────────────────── */}
      <ManualSectionHeading id="working" num={6} title="Working a prospect: from new lead to meeting set" />

      <ManualPara>
        Route — <code>/#/account/:id</code> (click any account name in the cadence queue or the
        ABM list). The account detail is the single-account truth screen. It shows the Composite
        score, the 10-checkpoint coverage grid, the activity timeline, and the Attilius
        coaching note for that specific account.
      </ManualPara>

      <ManualImage
        src={shot03}
        alt="Prospect detail with CP1-CP10 coverage grid"
        caption="Live screen: /#/account/:id — prospect detail with CP1-CP10 coverage grid"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        The lifecycle of a prospect — BDR scope
      </h3>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Stage 1: New lead arrival.</strong> Marketing drops an MQL into your queue, or you add a self-sourced prospect. The prospect arrives with zero answered questions.</span>,
          <span><strong style={{ color: BLACK }}>Stage 2: Cadence execution.</strong> You run the 7-touch program. Most prospects at this stage are in CP1 territory.</span>,
          <span><strong style={{ color: BLACK }}>Stage 3: First conversation.</strong> The prospect picks up or replies. Your goal: answer CP1 — Account Profile and begin CP2 — Compelling Pain. Open with the hook. Confirm CP1. Start CP2. Ask for the meeting. Book it on the call — do not say "I'll send you a link."</span>,
          <span><strong style={{ color: BLACK }}>Stage 4: Meeting confirmed.</strong> A meeting is on the calendar between the prospect and the AE. Your primary job shifts from outbound cadence to handoff preparation.</span>,
          <span><strong style={{ color: BLACK }}>Stage 5: Handoff prep.</strong> Complete the handoff record in Attilius before the AE meeting.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Reading the account detail screen
      </h3>

      <ManualPara>
        The account detail has seven tabs: Overview, Opportunity DNA, Decision Tree, Plan, Templates,
        Activity Log, and Coaching. As a BDR, your primary tabs are Overview and Activity Log.
      </ManualPara>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Composite score</strong> (top right card): the 0–100 weighted score derived under the M-PACT framework. As a BDR, you typically work accounts in the 0–40 range.</span>,
          <span><strong style={{ color: BLACK }}>Where We Are / Where We're Stuck / What Advances This:</strong> the three-column Attilius coaching card. Read "What Advances This" before every call.</span>,
          <span><strong style={{ color: BLACK }}>Checkpoints:</strong> the ten colored orbs (CP1-CP10). Green = answered and strong. Orange = partial. Red = disqualifying signal. Grey = not yet assessed. Aim to have CP1 and CP2 green before you book the meeting.</span>,
          <span><strong style={{ color: BLACK }}>Opportunity DNA tab:</strong> CP1 — Account Profile (9 questions) — target first 3–4. CP2 — Compelling Pain (11 questions) — target 3–5. CP3 — Budget (9 questions) — gather context only if the prospect volunteers budget information; target first 1–2 questions. Always use the prospect's actual words in the notes field.</span>,
        ]}
      />

      {/* ── Chapter 7 — Handoff ──────────────────────────────────────────────── */}
      <ManualSectionHeading id="handoff" num={7} title="The handoff: delivering a clean qualified prospect" />

      <ManualPara>
        The handoff is the moment where your work becomes the AE's foundation. A bad handoff costs
        the AE two hours of re-discovery. A good handoff accelerates the AE directly into CP3 — Budget
        and CP4 — Timeline territory. The AE expects four things from you.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 20, marginBottom: 8 }}>
        1. CP1 — Account Profile answered
      </h3>
      <ManualPara>
        There is a real, named project. Not "they expressed interest" — a project with a name, an
        initiative, a problem statement, and a defined scope. Good: "Ford Dearborn plant — VP
        Operations wants to replace the legacy supplier-qualification workflow because they are
        failing ISO audits on traceability. Project is live, internal name is Traceability Overhaul,
        decision expected before fiscal year-end." Not good: "Ford seems interested in what we do."
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 20, marginBottom: 8 }}>
        2. CP2 — Compelling Pain answered
      </h3>
      <ManualPara>
        The pain is named, quantified if possible, and in the prospect's exact words. Log the quote
        verbatim. If you cannot give the AE a pain statement in the prospect's words, the AE will
        spend the first 20 minutes of the discovery call re-confirming the pain.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 20, marginBottom: 8 }}>
        3. Decision maker identified
      </h3>
      <ManualPara>
        You have identified at least one person who is either the economic buyer or is connected to
        the economic buyer. A prospect where the only contact is a mid-level analyst with no budget
        ownership is a suspect, not a qualified prospect.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 20, marginBottom: 8 }}>
        4. Meeting booked on the calendar
      </h3>
      <ManualPara>
        The AE meeting is confirmed — it exists on a calendar, the prospect accepted the invite, and
        the AE knows about it. A meeting that is "tentative" is not booked.
      </ManualPara>

      <ManualImage
        src={shot05}
        alt="Account at Qualified Opp status, ready for AE handoff"
        caption="Live screen: /#/account/:id — account at Qualified Opp status, ready for AE handoff"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        How to complete the handoff in Attilius
      </h3>

      <StepList
        steps={[
          <span>Open the account detail at <code>/#/account/:id</code>.</span>,
          <span>Confirm the composite score is above 20. If below 20, continue working the cadence.</span>,
          <span>Confirm: CP1 — Account Profile has at least 2 green answers. CP2 — Compelling Pain has at least 2 green answers. One decision maker is mapped under the Decision Makers section.</span>,
          <span>Open the Activity Log tab. Add a handoff note: "Meeting booked for [date] with [AE name]. CP1: [project statement]. CP2: [pain statement in prospect's words]. Decision maker mapped: [name, title]. Source: [MQL / cold / event / referral]."</span>,
          <span>Set the account status to "Qualified Opp" using the status qualifier on the account detail Overview tab.</span>,
          <span>Notify the AE directly — a 2-sentence Slack message or email with the meeting details and the one-sentence pain statement is the standard.</span>,
        ]}
      />

      <ManualTable
        headers={["What you give the AE", "Where it lives in Attilius", "Minimum standard"]}
        rows={[
          ["Named project", "CP1 — Account Profile questions in Opportunity DNA", "2+ green answers"],
          ["Pain in prospect's words", "CP2 — Compelling Pain questions in Opportunity DNA", "2+ green answers, verbatim quote"],
          ["Decision maker", "Decision Makers section on Overview", "1 named, titled, logged"],
          ["Confirmed meeting", "Activity Log note + calendar", "Invite accepted, not tentative"],
          ["Source and context", "Activity Log handoff note", "One paragraph"],
        ]}
      />

      {/* ── Chapter 8 — Activity Log ─────────────────────────────────────────── */}
      <ManualSectionHeading id="activity-log" num={8} title="Your activity log: what to record and why" />

      <ManualPara>
        Route — <code>/#/log/bdr</code> (top nav: ACTIVITY LOG, or LOG ACTIVITY button on the
        dashboard). Five minutes at end of day, every day. The activity log is your accountability
        surface.
      </ManualPara>

      <ManualImage
        src={shot04}
        alt="BDR daily activity log"
        caption="Live screen: /#/log/bdr — Daily Activity Log"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        What to log
      </h3>

      <ManualPara>
        The log has three sections: Phone Activity, Email/LinkedIn, and Outcomes.
      </ManualPara>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Dials Made</strong> — total outbound calls placed, including no-answers, voicemails, and hangups. Every dial counts.</span>,
          <span><strong style={{ color: BLACK }}>Live Conversations</strong> — calls where the prospect picked up and you had a conversation of any length, even 30 seconds.</span>,
          <span><strong style={{ color: BLACK }}>Voicemails Left</strong> — only Step 6 has a scripted voicemail. If you left a voicemail at Step 2 or Step 4, that is outside cadence protocol.</span>,
          <span><strong style={{ color: BLACK }}>No-Message Hangups</strong> — correct action for Step 2 and Step 4.</span>,
          <span><strong style={{ color: BLACK }}>Emails Sent</strong> — total outbound emails sent from the cadence or outside it.</span>,
          <span><strong style={{ color: BLACK }}>LinkedIn DMs</strong> — direct messages only, not connection requests without a message.</span>,
          <span><strong style={{ color: BLACK }}>Meetings Booked</strong> — confirmed calendar meetings with an AE present.</span>,
          <span><strong style={{ color: BLACK }}>Meetings Held</strong> — meetings that happened as scheduled. This closes the booking-to-held conversion rate.</span>,
          <span><strong style={{ color: BLACK }}>No-Shows</strong> — log these honestly. The no-show rate tells you about meeting quality, not meeting quantity.</span>,
          <span><strong style={{ color: BLACK }}>Notes</strong> — anything that does not fit a number: a competitor name heard in conversation, a prospect who said "call me back in 90 days," objections heard more than once this week.</span>,
        ]}
      />

      <StepList
        steps={[
          <span>At end of every workday, open <code>/#/log/bdr</code>.</span>,
          <span>Confirm the date picker shows today.</span>,
          <span>Fill in all numeric fields. If a number is zero, enter zero — do not leave a blank.</span>,
          <span>Add a Notes entry for any substantive development.</span>,
          <span>Click <strong>Save Today's Log</strong>.</span>,
          <span>Glance at the 7-day trend. Two blanks in a row means plan for extra logging discipline the next two days.</span>,
        ]}
      />

      {/* ── Chapter 9 — Plan vs Actual ───────────────────────────────────────── */}
      <ManualSectionHeading id="plan-vs-actual" num={9} title="Plan vs actual: weekly rhythm and the leaderboard" />

      <ManualPara>
        Route — <code>/#/plan-vs-actual</code> (linked from the BDR dashboard footer). The Plan vs
        Actual screen is a team-level view. As a BDR, you see the full team performance. This is
        intentional — you need to see how your meetings are converting downstream.
      </ManualPara>

      <ManualImage
        src={shot06}
        alt="Plan vs Actual Q2 2026 — team attainment by AE with BDR contributions visible"
        caption="Live screen: /#/plan-vs-actual — team attainment, by AE, with BDR contributions visible"
      />

      <StepList
        steps={[
          <span>Open <code>/#/plan-vs-actual</code>. Default view is Quarter. Toggle Month for weekly check-ins, Year for strategic reads.</span>,
          <span>Read the dark stat strip first — Overall Attainment, Closed Quarter, Weighted Pipeline, Gap to Plan.</span>,
          <span>Read the four KPI cards: Quota Attainment, Pipeline Coverage, Deals Closed, and MQL Volume (where you and Marketing intersect).</span>,
          <span>Scan the By-AE table. If you booked five meetings with an AE and that AE has zero closed-won, audit the quality of the meetings. Were CP1 and CP2 answers solid.</span>,
          <span>Color rule: Green at 95% or above. Yellow 80–94%. Orange 60–79%. Red below 60%.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        How to use the leaderboard without gaming it
      </h3>

      <ManualPara>
        Gaming the leaderboard means booking meetings that are not real — conversations where there
        is no project, no pain, no decision maker — just to hit a meeting-booked number. It produces
        a short-term metric improvement and a medium-term pipeline collapse. The platform detects
        this pattern. If your meeting-to-qualified conversion rate is below 40%, the coaching card
        will surface it.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Weekly rhythm for a BDR
      </h3>

      <ManualTable
        headers={["Day", "What to do"]}
        rows={[
          ["Monday", "Open the BDR dashboard. Read the Morning Brief. Scan the cadence queue for overdue steps — clear all overdue before starting new steps."],
          ["Tue–Thu", "Execute the cadence queue. Aim for all scheduled steps completed by end of day. Log activity daily. Accept or return any new Marketing MQL within 24 hours."],
          ["Friday", "MQL reconciliation review with Marketing and Manager. Plan vs Actual check-in on dials, connects, and meetings. Close the week's activity log."],
        ]}
      />

      {/* ── Chapter 10 — Glossary ────────────────────────────────────────────── */}
      <ManualSectionHeading id="glossary" num={10} title="Glossary, common gotchas, and closing note" />

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M-PACT</strong> — the Mi6 operating model. M = Methodology. P = Process / Prioritization. A = Accountability. C = Cadence / Coaching. T = Timing / Timeline management.</span>,
          <span><strong style={{ color: BLACK }}>CP1 — Account Profile.</strong> Is there a real project, and is this a new logo or an existing customer expansion.</span>,
          <span><strong style={{ color: BLACK }}>CP2 — Compelling Pain.</strong> Pain named, quantified, in the prospect's own words.</span>,
          <span><strong style={{ color: BLACK }}>CP3 — Budget.</strong> Budget context gathered only if the prospect volunteers it. The AE validates the named budget source and amount.</span>,
          <span><strong style={{ color: BLACK }}>MQL</strong> — Marketing Qualified Lead. A lead Marketing has surfaced. Accept or return within 24 hours.</span>,
          <span><strong style={{ color: BLACK }}>SQL</strong> — Sales Qualified Lead. A lead you have accepted and qualified into a meeting. Your work product.</span>,
          <span><strong style={{ color: BLACK }}>Cadence</strong> — the 7-touch outbound program. Auto-progression means you mark done and the system advances. Step 7 is a pursuit decision, not a contact action.</span>,
          <span><strong style={{ color: BLACK }}>Firm / formal timeline</strong> — a date the buyer has committed to. Always lives under CP4 — Timeline.</span>,
          <span><strong style={{ color: BLACK }}>Compelling Reason to Buy</strong> — the business case that makes the decision urgent for the prospect. Must be grounded in the prospect's named pain and stated timeline.</span>,
          <span><strong style={{ color: BLACK }}>Helix</strong> — the Attilius diagnostic visualization at <code>/#/opportunity-dna</code>. As a BDR, you read the helix to understand account health. The AE drives it.</span>,
          <span><strong style={{ color: BLACK }}>Two-mark rule</strong> — Attilius is the elephant. The helix is the diagnostic. Do not mix the two marks.</span>,
          <span><strong style={{ color: BLACK }}>ABM</strong> — Account-Based Marketing. The Top 10 ABM list is your primary territory.</span>,
        ]}
      />

      <ManualTable
        headers={["CP", "Name", "BDR scope", "AE scope"]}
        rows={[
          ["CP1", "Account Profile", "Confirm project exists, name it", "Deepen, confirm new logo vs expansion"],
          ["CP2", "Compelling Pain", "Extract pain in prospect's own words", "Quantify, map to business impact"],
          ["CP3", "Budget", "Context only if volunteered", "Validate named budget source and amount"],
          ["CP4", "Timeline", "Context only if volunteered", "Confirm decision and implementation window"],
          ["CP5", "Strategic Fit", "Note any project lead names", "Confirm fit for this customer's industry, size, and operating profile"],
          ["CP6", "Solution Fit", "Note any names mentioned", "Confirm technical evaluation and financial approval checkpoints"],
          ["CP7", "Competitive Landscape", "Name one contact", "Map full stakeholder landscape"],
          ["CP8", "Decision Process", "Note any names mentioned", "Full competitive assessment and stakeholder map"],
          ["CP9", "Risk and Stakeholder Alignment", "Not in scope", "Compliance requirements, approval path"],
          ["CP10", "Commitment to Buy", "Not in scope", "MSA, redlines, paper path"],
        ]}
      />

      <ManualPara>
        Marketing holds approval on all hooks. AEs cannot send a hook to a prospect until Marketing
        approves it. Submit the hook request in Attilius and wait for the approval status to update
        before sending.
      </ManualPara>

      <Callout>
        <strong style={{ color: BLACK }}>Common gotchas.</strong> A meeting exists when the prospect has accepted a calendar invite with a specific date and time. Do not compress steps 1–3 into 48 hours. "Qualified" means CP1 — Account Profile green and CP2 — Compelling Pain started — not "seems interested." Log the absence of a firm timeline — a deal with no urgency is a park-90 candidate. Read the Morning Brief daily. CP3 is Budget, not Pain — gather budget context only if the prospect volunteers it; do not ask directly.
      </Callout>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Closing note
      </h3>
      <ManualPara>
        You are the first voice in the funnel. Nothing the AE does, the Manager tracks, or Marketing
        generates matters if the meeting is not on the calendar and the prospect is not genuinely
        ready. Run the cadence, log the activity, answer CP1 and CP2 before you book, and hand off
        clean. The platform handles the sequencing, the scoring, and the coaching. Your job is the
        conversation. Do the work, and the work will show.
      </ManualPara>

      <Callout>
        <strong style={{ color: BLACK }}>Closing note.</strong> The helix is the diagnostic.
        The path is the play.
      </Callout>
    </ManualLayout>
  );
}
